import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {createRefetchContainer, graphql} from 'react-relay';
import {withRouter} from 'react-router-dom/cjs/react-router-dom';
import {FILTER_SECTIONS} from '../../shared/components/filters/FilterWrapper';
import {getFiltersAlphabetically} from '../../shared/components/filters/filter_util';
import {buildThirdLevelNavHeader} from '../../shared/components/headers/header-bar/header_bar';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../shared/components/headers/top-header-bar/TopHeaderBar';
import {GROUP_TYPE, TaskTable} from '../../shared/components/task-tables/task-table-v3/TaskTable';
import {TYPES} from '../../shared/components/task-tables/task-table-v3/Theme/NodeContentRenderer';
import {
	BUDGET_TYPE,
	BUTTON_COLOR,
	BUTTON_STYLE,
	ELEMENT_TYPE,
	ESTIMATION_UNIT,
	FILTER_SECTION,
	FILTER_TYPE,
	projectUrl,
} from '../../../constants';
import {hasFeatureFlag} from '../../shared/util/FeatureUtil';
import CreatePhaseMutation from '../../../mutations/create_phase_mutation';
import {withSocketHandling} from '../../../socket/withSocketHandling';
import * as tracking from '../../../tracking';
import Util from '../../shared/util/util';
import {MODAL_TYPE, showModal} from '../../shared/components/modals/generic_modal_conductor';
import {createToast} from '../../shared/components/toasts/toast';
import GenericTaskContextMenu from '../../shared/components/context-menus/generic_task_context_menu';
import {
	getExpandedPhasesMap,
	getInitialSelectedColumns,
	getInitialSelectedTab,
	getOrderedPhaseIds,
	getPhasesFilters,
	tabs,
	getInitialGroupType,
	updateSelectTasks,
} from './ProjectScopingUtil';
import {canDrag, getCanDropFunc, onMoveNode} from '../../shared/components/task-tables/task-table-v3/table-util/TableLogic';
import {
	addExtraDataToFixedPriceProject,
	generateTree,
	getProjectTotalData,
	getSharedOptions,
	phaseActiveFilter,
} from '../../shared/components/task-tables/task-table-v3/table-util/TableUtil';
import ProjectUtil from '../../shared/util/project_util';
import {getSocketConfig} from './ProjectScopingSocket';
import {useDataExport} from '../../shared/hooks/useDataExport';
import TaskFormatter from '../../shared/util/export-formatters/TaskFormatter';
import {hasSomePermission, hasPermission} from '../../shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import ProjectScopingTotals from './totals-header/ProjectScopingTotals';
import {useForecastFetchQuery} from '../../shared/hooks/useForecastFetchQuery';
import {scopingPredictionsQuery} from './ProjectScopingPredictionsQuery';
import {shouldShowTaskOverrun, trackPredictionsShown} from '../../shared/util/PredictionUtil';
import {keyBy} from 'lodash';
import {ProjectScopingTotalsProvider} from './totals-header/ProjectScopingTotalsContext';
import {BulkSelectPopup} from 'web-components';
import {getComplexBulkOptions} from '../../shared/util/BulkUtil';
import {getAdditionalBulkUpdateAutomateOptions, getBulkUpdateOptions} from './ProjectScopingBulkLogic';

export const ProjectScopingPage = ({
	viewer,
	route,
	history,
	router,
	relay,
	refetch,
	retry,
	buyNowTime,
	setSocketVariables,
	setSocketConfig,
}) => {
	const intl = useIntl();
	const shouldShowOverrunPrediction = shouldShowTaskOverrun(
		viewer.project.estimationUnit === 'HOURS',
		PERMISSION_TYPE.MILESTONE_UPDATE,
		viewer.availableFeatureFlags
	);
	// eslint-disable-next-line
	const [lazyDataFetched, setLazyDataFetched] = useState(true);
	const [orderedPhaseIds, setOrderedPhaseIds] = useState(
		getOrderedPhaseIds(viewer.project.phases.edges.map(phase => phase.node))
	);
	const [phasesExpansionMap, setPhasesExpansionMap] = useState(
		getExpandedPhasesMap(viewer.project.phases.edges.map(phase => phase.node))
	);
	const [tasksExpansionMap, setTasksExpansionMap] = useState(new Map());
	const [personGroupExpansionMap, setPersonGroupExpansionMap] = useState(new Map());
	const [roleGroupExpansionMap, setRoleGroupExpansionMap] = useState(new Map());
	const [selectedTab, setSelectedTab] = useState(getInitialSelectedTab());
	const [filterFunctions, setFilterFunctions] = useState(getPhasesFilters(viewer.project.id));
	const [searchFilterValue, setSearchFilterValue] = useState('');
	// saving state in ref also
	const [selectedTasks, setSelectedTasks] = useState([]);
	// The eye
	const [showBaselineInfo, setShowBaselineInfo] = useState(
		viewer.project.estimationUnit === ESTIMATION_UNIT.HOURS &&
			Util.isBaselineProject(viewer.company.modules, viewer.project)
			? JSON.parse(localStorage.getItem('project-phases-show-baseline-info' + viewer.project.companyProjectId)) ?? true
			: false
	);
	// const [projectTotalExpanded, setProjectTotalExpanded] = useState(true);
	const [groupSetting, setGroupSetting] = useState(getInitialGroupType());

	// const [showEmptyPersons, setShowEmptyPersons] = useState(false);

	const SHOW_PERIODS_SETTING = 'project-scoping-show-periods';
	const SHOW_EMPTY_PERIODS_SETTING = 'project-scoping-show-empty-periods';

	/**
	 * Is this a retainer project, and are you allowed to see it.
	 *
	 * @type {boolean} Show the retainer project options
	 */
	const isRetainerProject =
		hasSomePermission([PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION, PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION_REVENUE]) &&
		viewer.project.budgetType === BUDGET_TYPE.RETAINER;

	const [showPeriods, setShowPeriods] = useState(
		localStorage.getItem(SHOW_PERIODS_SETTING)
			? JSON.parse(localStorage.getItem(SHOW_PERIODS_SETTING)) && isRetainerProject
			: false
	);
	const [showEmptyPeriods, setShowEmptyPeriods] = useState(
		localStorage.getItem(SHOW_EMPTY_PERIODS_SETTING)
			? JSON.parse(localStorage.getItem(SHOW_EMPTY_PERIODS_SETTING)) && isRetainerProject
			: true
	);

	const [showContextMenu, setShowContextMenu] = useState(false);
	const [contextMenuPosition, setContextMenuPosition] = useState(null);
	const [contextMenuTask, setContextMenuTask] = useState(null);
	const [createSubtaskTask, setCreateSubtaskTask] = useState(null);

	const [availableColumns, setAvailableColumns] = useState([]); // Consider refactoring to new approach to the eye

	useEffect(() => {
		const initialSelectedColumns = getInitialSelectedColumns(viewer, showPeriods);
		setAvailableColumns(initialSelectedColumns);
	}, [showPeriods]);

	const {fetch, data: predictionData} = useForecastFetchQuery(scopingPredictionsQuery);

	const exportData = useDataExport(
		new TaskFormatter(
			intl,
			['phase_name'],
			[
				'chip_right',
				'expand',
				'milestone',
				'selector',
				'starred',
				'context_menu',
				'show_scoping_project_total',
				'show_overrun_prediction',
			],
			true
		),
		null,
		data => data,
		_ => `${viewer.project.name}_scoping_data`
	);

	const currencySymbol = Util.GetCurrencySymbol(
		viewer.project.rateCard ? viewer.project.rateCard.currency : viewer.company.currency
	);

	const predictionDataTasks = useMemo(() => {
		return (
			predictionData &&
			(predictionData.viewer.projectGroup
				? predictionData.viewer.projectGroup.projects.edges
						.reduce((acc, project) => acc.concat(project.node.tasks ? project.node.tasks.edges : []), [])
						.map(predTask => predTask.node)
				: predictionData.viewer.project.tasks.edges.map(predTask => predTask.node))
		);
	}, [predictionData]);

	const predictionDataMap = useMemo(() => {
		return predictionDataTasks && keyBy(predictionDataTasks, o => o.id);
	}, [predictionDataTasks]);

	useEffect(() => {
		const name =
			viewer.project.name !== null && viewer.project.name !== ''
				? viewer.project.name
				: 'P-' + viewer.project.companyProjectId;
		document.title = 'Scoping - ' + name + ' - Forecast';
		tracking.trackPage('project-scoping-v2');
		setSocketConfig(getSocketConfig(viewer.project.id, viewer.actualPersonId));
		if (shouldShowOverrunPrediction) {
			fetch({projectId: '' + viewer.project.companyProjectId});
		}
	}, []);

	useEffect(() => {
		if (predictionData) {
			trackPredictionsShown(predictionDataTasks);
		}
	}, [predictionDataTasks]);

	const toggleCollapseAll = shouldCollapse => {
		const map = new Map();
		phasesExpansionMap.forEach((v, k) => map.set(k, !shouldCollapse));
		Util.localStorageSetItem(viewer.project.id + '-map', JSON.stringify(map));
		setPhasesExpansionMap(map);
	};

	const togglePhaseExpanded = phaseId => {
		const map = new Map(phasesExpansionMap);
		const curVal = map.get(phaseId);
		map.set(phaseId, !curVal);
		Util.localStorageSetItem(viewer.project.id + '-map', JSON.stringify(map));
		setPhasesExpansionMap(map);
	};

	const toggleTaskExpanded = taskId => {
		const map = new Map(tasksExpansionMap);
		const curVal = map.get(taskId);
		map.set(taskId, !curVal);

		setTasksExpansionMap(map);
		if (taskId === createSubtaskTask) {
			setCreateSubtaskTask(null);
		}
	};

	/**
	 * Toggle expanded on person group
	 * @param  personGroupId
	 */
	const togglePersonGroupExpanded = personGroupId => {
		const map = new Map(personGroupExpansionMap);
		let curVal = map.get(personGroupId);
		//curVal will be undefined the first time the group is toggled
		//and by default groups are expanded
		if (curVal === undefined) {
			map.set(personGroupId, false);
		} else {
			map.set(personGroupId, !curVal);
		}
		setPersonGroupExpansionMap(map);
	};

	/**
	 * Toggle expanded on role group
	 * @param  roleGroupId
	 */
	const toggleRoleGroupExpanded = roleGroupId => {
		const map = new Map(roleGroupExpansionMap);
		let curVal = map.get(roleGroupId);
		//curVal will be undefined the first time the group is toggled
		//and by default groups are expanded
		if (curVal === undefined) {
			map.set(roleGroupId, false);
		} else {
			map.set(roleGroupId, !curVal);
		}
		setRoleGroupExpansionMap(map);
	};

	const toggleExpansion = useCallback(
		({node}) => {
			if (node.type === TYPES.HEADER) {
				togglePhaseExpanded(node.id);
			} else if (node.type === TYPES.TASK) {
				toggleTaskExpanded(node.id);
			} else if (node.type === TYPES.PERSON_GROUPING) {
				togglePersonGroupExpanded(node.id);
			} else if (node.type === TYPES.ROLE_GROUPING) {
				if (showBaselineInfo && node.phaseId !== 'no-milestone') {
					toggleRoleGroupExpanded(node.role.id + '#' + node.phaseId);
				} else {
					toggleRoleGroupExpanded(node.id);
				}
			}
		},
		[phasesExpansionMap, tasksExpansionMap, createSubtaskTask, personGroupExpansionMap, roleGroupExpansionMap]
	);

	const showTaskModal = useCallback(
		taskId => {
			Util.showTaskModal(taskId, history);
		},
		[history]
	);

	const handleRowSelected = useCallback(
		(taskToSelect, shiftKey) => {
			updateSelectTasks(viewer, intl, selectedTasks, taskToSelect, shiftKey, createToast, setSelectedTasks, groupSetting);
		},
		[viewer, selectedTasks, groupSetting]
	);

	const closeContextMenu = () => {
		if (showContextMenu) {
			setShowContextMenu(false);
			setContextMenuPosition(null);
			setContextMenuTask(null);
		}
	};

	const onContextMenu = useCallback((task, taskProject, e) => {
		const el = e.target;
		const projectStatus = taskProject ? taskProject.status : '';
		if (projectStatus === 'DONE' || projectStatus === 'HALTED') {
			return;
		}
		//clicked is when the context menu is open from the 3 dot-options on table-row
		if (
			el &&
			(el.tagName === 'INPUT' ||
				el.tagName === 'BUTTON' ||
				el.tagName === 'A' ||
				el.className.toLowerCase().includes('clicked'))
		) {
			closeContextMenu();
			return;
		}
		e.preventDefault();

		const contextMenuPosition = {};

		const btn = document.elementFromPoint(e.pageX, e.pageY);
		if (btn != null && e.type !== 'contextmenu') {
			const btnBounds = btn.getBoundingClientRect();
			contextMenuPosition.x = btnBounds.right - btn.clientWidth - 2;
			if (window.innerHeight - btnBounds.bottom < 250) {
				contextMenuPosition.y = btnBounds.bottom - 282;
			} else {
				contextMenuPosition.y = btnBounds.bottom + 2;
			}
		} else {
			contextMenuPosition.x = e.pageX;
			//check if there is place for menu underneath cursor
			if (window.innerHeight - e.pageY < 250) {
				contextMenuPosition.y = e.pageY - 250;
			} else {
				contextMenuPosition.y = e.pageY;
			}
		}
		setShowContextMenu(true);
		setContextMenuPosition(contextMenuPosition);
		setContextMenuTask(task);
	}, []);

	const onCreateSubtask = useCallback(
		taskId => {
			if (!tasksExpansionMap.get(taskId)) {
				toggleTaskExpanded(taskId);
			}
			setCreateSubtaskTask(createSubtaskTask === taskId ? null : taskId);
		},
		[createSubtaskTask, tasksExpansionMap]
	);

	const isCreatingPhase = useRef(false);

	const phases = useMemo(() => {
		return viewer.project.phases.edges
			.map(phase => phase.node)
			.filter(phase => phaseActiveFilter(phase, selectedTab))
			.sort((a, b) => {
				return orderedPhaseIds.indexOf(a.id) - orderedPhaseIds.indexOf(b.id);
			});
	}, [viewer.project.phases.edges, selectedTab]);

	const sharedOptions = useMemo(() => {
		const visibility = {
			showBaselineInfo,
			showPeriods: showPeriods,
			showEmptyPeriods: showEmptyPeriods,
		};

		return getSharedOptions(
			viewer,
			viewer.projectGroup,
			availableColumns,
			visibility,
			phases,
			createSubtaskTask,
			showTaskModal,
			onContextMenu,
			handleRowSelected,
			onCreateSubtask,
			predictionData,
			selectedTasks.length > 0,
			intl
		);
	}, [
		viewer,
		selectedTasks,
		availableColumns,
		showBaselineInfo,
		phases,
		createSubtaskTask,
		tasksExpansionMap,
		predictionData,
		showPeriods,
		showEmptyPeriods,
	]);

	const treeData = useMemo(() => {
		return generateTree(
			viewer,
			phases,
			phasesExpansionMap,
			tasksExpansionMap,
			selectedTasks,
			availableColumns,
			groupSetting,
			filterFunctions,
			searchFilterValue,
			predictionDataMap,
			sharedOptions,
			intl,
			personGroupExpansionMap,
			roleGroupExpansionMap
		);
	}, [
		viewer,
		phases,
		phasesExpansionMap,
		tasksExpansionMap,
		personGroupExpansionMap,
		roleGroupExpansionMap,
		availableColumns,
		groupSetting,
		filterFunctions,
		searchFilterValue,
		predictionDataMap,
		selectedTasks,
		sharedOptions,
		showPeriods,
	]);

	const projectTotalData = useMemo(() => {
		return getProjectTotalData(treeData, viewer.project, viewer.availableFeatureFlags);
	}, [treeData, viewer.project, viewer.availableFeatureFlags]);

	const handleOnEscapeKeydown = () => {
		setSelectedTasks([]);
	};

	const handleKeyDownListener = useCallback(
		e => {
			const {key} = e;
			switch (key) {
				case 'Escape':
					handleOnEscapeKeydown();
					break;
			}
		},
		[selectedTasks]
	);

	useEffect(() => {
		window.addEventListener('keydown', handleKeyDownListener);
		return () => {
			window.removeEventListener('keydown', handleKeyDownListener);
		};
	}, []);
	useEffect(() => {
		tracking.trackPageLoad('Scoping view', {tab_selected: selectedTab.value});

		if (refetch) {
			refetch(
				{
					fetchLazyData: true,
				},
				null,
				() => setLazyDataFetched(true)
			);
		}
	}, []);

	const showAutomateModal = variant => {
		showModal({
			type: MODAL_TYPE.PROJECT_AUTOMATE,
			variant: variant,
			tasks: selectedTasks,
			intl: intl,
			allProjects: viewer.projects,
			currentProject: viewer.project,
			labels: viewer.company.labels,
			roles: viewer.company.roles,
			persons: viewer.project.projectPersons.edges.map(edge => edge.node.person),
		});
	};

	const addPhase = () => {
		const onSuccess = result => {
			isCreatingPhase.current = false;
			const orderedPhases = [result.createPhase.phase.node.id, ...orderedPhaseIds];

			const expansionMap = new Map(phasesExpansionMap);
			expansionMap.set(result.createPhase.phase.node.id, true);

			setOrderedPhaseIds(orderedPhases);
			setPhasesExpansionMap(expansionMap);

			createToast({duration: 5000, message: intl.formatMessage({id: 'project_scopes.scope-group-created'})});
			//scroll to the new milestone
		};
		//const formattedDeadline = Util.GetYearMonthDateFromMomentDate(deadline);
		if (isCreatingPhase.current === false) {
			isCreatingPhase.current = true;
			Util.CommitMutation(
				CreatePhaseMutation,
				{
					projectId: viewer.project.id,
					name: intl.formatMessage({id: 'project_scoping.new-scope-group'}),
				},
				onSuccess
			);
		}
	};

	const theEyeSelector = selectedElem => {
		const cols = [...availableColumns];
		cols.forEach(elem => {
			if (elem.name === selectedElem.name) {
				elem.checked = !elem.checked;
			}
		});

		// custom logic for showing base line info.
		// this is project specific, and not a global setting
		// that is why we save it in its own localStorage
		let baselineInfoOption = showBaselineInfo;

		if (selectedElem.name === 'show-scoping-baseline-info') {
			baselineInfoOption = !showBaselineInfo;
		}

		setShowBaselineInfo(baselineInfoOption);
		setAvailableColumns(cols);

		const theEye = cols.map(c => {
			return {
				id: c.name,
				checked: c.checked,
			};
		});

		localStorage.setItem(`project-phases-show-baseline-info${viewer.project.companyProjectId}`, baselineInfoOption);
		Util.localStorageSetItem('project-new-scoping-selected-cols', JSON.stringify(theEye));
		tracking.trackEyeSelector(
			cols
				.filter(elem => elem.name !== 'chip-right' && elem.name !== 'selector' && elem.name !== 'task-name')
				.map(c => {
					return {
						name: c.name,
						checked: c.checked,
					};
				})
		);
	};

	/*
	toggleShowEmptyPersons() {
		this.setState(prevState => {
			return {showEmptyPersons: !prevState.showEmptyPersons};
		});

		tracking.trackElementClicked('Show empty persons', {enabled: this.state.showEmptyPersons});
	}*/

	const toggleGroupByPerson = () => {
		// Deselect tasks when changing grouping
		setSelectedTasks([]);
		const toggleOn = groupSetting !== GROUP_TYPE.PERSON;
		setGroupSetting(toggleOn ? GROUP_TYPE.PERSON : GROUP_TYPE.NO_GROUPING);

		Util.localStorageSetItem('project-scoping-group-by-person', toggleOn);
		Util.localStorageSetItem('project-scoping-group-by-role', false);

		tracking.trackElementClicked('Group by person', {enabled: toggleOn});
	};

	const toggleGroupByRole = () => {
		// Deselect tasks when changing grouping
		setSelectedTasks([]);
		const toggleOn = groupSetting !== GROUP_TYPE.ROLE;
		setGroupSetting(toggleOn ? GROUP_TYPE.ROLE : GROUP_TYPE.NO_GROUPING);

		Util.localStorageSetItem('project-scoping-group-by-role', toggleOn);
		Util.localStorageSetItem('project-scoping-group-by-person', false);

		tracking.trackElementClicked('Group by role', {enabled: toggleOn});
	};

	const toggleShowPeriods = () => {
		setShowPeriods(value => {
			const newValue = !value;
			Util.localStorageSetItem(SHOW_PERIODS_SETTING, newValue);
			tracking.trackElementClicked('Show Periods', {enabled: newValue});
			return newValue;
		});
	};

	const toggleShowEmptyPeriods = () => {
		setShowEmptyPeriods(value => {
			const newValue = !value;
			Util.localStorageSetItem(SHOW_EMPTY_PERIODS_SETTING, newValue);
			tracking.trackElementClicked('Show Empty Periods', {enabled: newValue});
			return newValue;
		});
	};

	const handleTabChange = tab => {
		setSelectedTab(tab);
		Util.localStorageSetItem('project-scoping-selected-tab', tab.value);
		tracking.trackElementClicked(tab.value + ' milestones');
	};

	const onAutoScheduleButtonClicked = () => {
		tracking.trackElementClicked('Auto Schedule project from the Scoping page');
		//  open new tab on the project timeline and with the autoschedule modal
		window.open(`${projectUrl(viewer.project.companyProjectId)}/schedule#auto-schedule`);
	};

	const dataCSV = () => {
		const isFixedPriceProject = viewer.project.budgetType === BUDGET_TYPE.FIXED_PRICE_V2;
		const allTasks = isFixedPriceProject ? addExtraDataToFixedPriceProject(viewer.project) : viewer.project.tasks.edges;

		let csvTasks = allTasks.map(task => task.node);
		csvTasks = csvTasks.sort((a, b) => {
			const aPhase = a.phase ? a.phase : null;
			const bPhase = b.phase ? b.phase : null;
			const samePhase = aPhase && bPhase && aPhase.id === bPhase.id;

			// Both are in same milestone, compare sort order
			if ((aPhase === null && bPhase === null) || samePhase) {
				return a.sortOrder - b.sortOrder;
			} else if (aPhase == null && bPhase) {
				return 1;
			} else if (aPhase && bPhase == null) {
				return -1;
			}
			//	Else sort by milestone
			const aStartDate = aPhase.startYear
				? Util.CreateMomentDate(aPhase.startYear, aPhase.startMonth, aPhase.startDay)
				: null;
			const bStartDate = bPhase.startYear
				? Util.CreateMomentDate(bPhase.startYear, bPhase.startMonth, bPhase.startDay)
				: null;

			const sameDate = aStartDate && bStartDate && aStartDate.isSame(bStartDate);
			// Both milestones have the same start date, sort by milestone id
			if ((aStartDate == null && bStartDate == null) || sameDate) {
				return aPhase.id < bPhase.id ? 1 : -1;
			} else if (aStartDate == null && bStartDate) {
				return 1;
			} else if (aStartDate && bStartDate == null) {
				return -1;
			} else return aStartDate.isBefore(bStartDate) ? -1 : 1;
		});
		return csvTasks;
	};

	const exportCSVNew = () => {
		// Clone columns and replace - to _ in all columns to match CSV formatter.
		let columns = JSON.parse(JSON.stringify(availableColumns));
		columns.forEach(column => column.name = column.name.replace('-', '_'));
		exportData(columns, dataCSV(), {projects: [viewer.project]}, null, 'CSV');
	};

	const exportCSV = () => {
		const {formatNumber, formatDate} = intl;
		const dataPreFormatting = dataCSV();

		const data = [];
		const isEstimatedInHours = viewer.project.estimationUnit === ESTIMATION_UNIT.HOURS;
		const minsPerPoint = viewer.project.minutesPerEstimationPoint;

		const fixedPriceData = ProjectUtil.getFixedPriceData(viewer.project, viewer.availableFeatureFlags);

		const convertedEstimate = num => {
			return isEstimatedInHours ? num : formatNumber((num * minsPerPoint) / 60, {format: 'rounded_two_decimal'});
		};

		// Filter out unchecked columns, and irrelevant columns like starred, expand, etc.
		const filteredColumns = availableColumns.filter(
			c =>
				c.checked &&
				![
					'chip-right',
					'expand',
					'milestone',
					'selector',
					'starred',
					'context-menu',
					'show-scoping-project-total',
					'show-overrun-prediction',
				].includes(c.name)
		);

		dataPreFormatting.forEach(t => {
			const d = {
				'milestone-name': t.phase ? '"' + t.phase.name + '"' : 'No milestone',
			};
			filteredColumns.forEach(c => {
				let val;
				const tr = t.timeRegistrations.edges.reduce(
					(totalTime, tmpTimeReg) => totalTime + tmpTimeReg.node.minutesRegistered,
					0
				);
				switch (c.name) {
					case 'task-id':
						val = 'T' + t.companyTaskId;
						break;
					case 'task-name':
						val = '"' + t.name + '"';
						break;
					case 'date':
						const dateString =
							(t.startDay
								? formatDate(Util.CreateNonUtcMomentDate(t.startYear, t.startMonth, t.startDay), {
										month: 'short',
										day: 'numeric',
								  })
								: '') +
							' - ' +
							(t.deadlineDay
								? formatDate(Util.CreateNonUtcMomentDate(t.deadlineYear, t.deadlineMonth, t.deadlineDay), {
										month: 'short',
										day: 'numeric',
								  })
								: '');
						val = dateString;
						break;
					case 'assigned-person':
						if (t.assignedPersons && t.assignedPersons.length > 0) {
							val = t.assignedPersons
								.map(a => a.firstName + (a.lastName !== '' ? ' ' + a.lastName : ''))
								.toString();
						} else {
							val = '';
						}
						break;
					case 'assigned-role':
						if (t.role) {
							val = t.role.name;
						} else {
							val = '';
						}
						break;
					case 'done-percentage':
						val = t.progress ? t.progress + '%' : '0%';
						break;
					case 'low-estimate':
						val = t.estimateLow != null ? convertedEstimate(t.estimateLow) : '';
						break;
					case 'high-estimate':
						val = t.estimateHigh != null ? convertedEstimate(t.estimateHigh) : '';
						break;
					case 'forecast':
						val = t.estimateForecast != null ? convertedEstimate(t.estimateForecast) : '';
						break;
					case 'time-entries':
						val = tr ? tr : '0';
						break;
					case 'remaining':
						val = t.timeLeft !== null ? convertedEstimate(t.timeLeft) : '';
						break;
					case 'status':
						val = t.statusColumnV2.name;
						break;
					case 'over-high':
						val =
							t.estimateHigh !== null && tr !== null
								? isEstimatedInHours
									? t.estimateHigh - tr
									: formatNumber((t.estimateHigh * minsPerPoint - tr) / 60, {format: 'rounded_two_decimal'})
								: '';
						break;
					case 'over-forecast':
						val =
							t.estimateForecast !== null && tr != null
								? isEstimatedInHours
									? t.estimateForecast - tr
									: formatNumber((t.estimateForecast * minsPerPoint - tr) / 60, {
											format: 'rounded_two_decimal',
									  })
								: '';
						break;
					case 'price':
						val = formatNumber(ProjectUtil.getEstimatedPrice(t, fixedPriceData), {format: 'always_two_decimal'});

						break;
					case 'actual-price':
						val = formatNumber(ProjectUtil.getActualPriceForTask(t, fixedPriceData), {
							format: 'always_two_decimal',
						});
						break;
					case 'sprint':
						val = t.sprint ? t.sprint.name : 'Backlog';
						break;
					case 'approved':
						val = t.approved ? 'Approved' : 'Unapproved';
						break;
					case 'labels':
						if (t.taskLabels && t.taskLabels.length > 0) {
							val = t.taskLabels.map(l => l.label.name).toString();
						} else {
							val = '';
						}
						break;
					default:
						break;
				}
				if (c.name.includes('forecast')) {
					if (c.name.includes('over')) {
						d[c.name.replace('over', 'difference').replace('forecast', 'estimate')] = val;
					} else {
						d[c.name.replace('forecast', 'estimate')] = val;
					}
				} else if (c.name.includes('price')) {
					d[c.name.replace('price', 'revenue')] = val;
				} else if (c.name.includes('over')) {
					d[c.name.replace('over', 'difference').replace('forecast', 'estimate')] = val;
				} else {
					d[c.name] = val;
				}
			});
			data.push(d);
		});

		const columnNames = ['milestone-name'];
		filteredColumns.forEach(c => {
			if (c.name.includes('forecast')) {
				if (c.name.includes('over')) {
					columnNames.push(c.name.replace('over', 'difference').replace('forecast', 'estimate'));
				} else {
					columnNames.push(c.name.replace('forecast', 'estimate'));
				}
			} else if (c.name.includes('price')) {
				columnNames.push(c.name.replace('price', 'revenue'));
			} else if (c.name.includes('over')) {
				columnNames.push(c.name.replace('over', 'difference').replace('forecast', 'estimate'));
			} else {
				columnNames.push(c.name);
			}
		});
		let exportData = columnNames + '\r\n';
		exportData += Util.JSONToCSV(data);
		const fileName = viewer.project.name.replace(' ', '_').toLowerCase() + '_scoping_data.csv';
		Util.exportToCSV(exportData, fileName);
	};

	const getHeaderTitle = () => {
		const content = [];
		const onboardingFlows = [
			{
				id: 'scoping-introduction',
				title: 'Scoping Intro',
				description: null,
				contentId: '1594304052lNpm0692',
			},
			{
				id: 'scoping-first-milestone',
				title: 'Scoping - Scoping out your first Milestone',
				description: null,
				contentId: '1594304249aHmt209',
			},
			{
				id: 'scoping-best-practices',
				title: 'Scoping - Tips and Tricks',
				description: null,
				contentId: '1594304278jNny2762',
			},
		];

		const onboardingComponent = {
			id: 'onboarding-component',
			intl: intl,
			type: TopHeaderBar.TYPE.ONBOARDING,
			title: intl.formatMessage({id: 'onboarding.scoping_onboarding_title'}),
			options: onboardingFlows,
		};
		content.push(onboardingComponent);

		// Should the new Scoping page be used as default? - Remove when switching completely!
		const useNewScopingAsDefault = hasFeatureFlag('task_hierarchy_part2', viewer.availableFeatureFlags);

		if (!useNewScopingAsDefault) {
			content.push({
				type: TopHeaderBar.TYPE.BETA,
			});

			content.push({
				type: TopHeaderBar.TYPE.FEEDBACK,
				userEmail: viewer.email,
				link: 'https://www.forecast.app/feedback-subtasks',
			});
		}

		return (
			<TopHeaderBarWrapper sidePadding={24}>
				<TopHeaderBar title={intl.formatMessage({id: 'project_section.scope'})} content={content} />
			</TopHeaderBarWrapper>
		);
	};

	const constructHeaderBar = () => {
		const parentGroup = viewer.project && viewer.project.projectGroupId;

		const projectLocked = viewer.project.status === 'DONE' || viewer.project.status === 'HALTED';
		const createMilestoneAccess = hasPermission(PERMISSION_TYPE.MILESTONE_CREATE);
		const {formatMessage} = intl;
		const leftContent = [];
		const rightContent = [];

		const autoSchedulingEnabled = !viewer.project.useManualAllocations && hasPermission(PERMISSION_TYPE.PROJECTS_UPDATE);
		tabs.forEach(tab => {
			tab.label = formatMessage({id: tab.translationId});
		});
		const pastOrActiveMilestone = {
			type: ELEMENT_TYPE.LEFT_DROPDOWN,
			dropdownOptions: tabs,
			value: selectedTab.value,
			buttonCy: 'new-ui-scoping-milestone-selector-tab',
			listDataCy: 'new-ui-scoping-milestone-selector-options',
			userpilot: 'milestone-dropdown',
			callback: tab => handleTabChange(tab),
		};
		leftContent.push(pastOrActiveMilestone);

		const theEye = {
			type: ELEMENT_TYPE.THE_EYE,
			options:
				viewer.project.estimationUnit === ESTIMATION_UNIT.HOURS &&
				Util.isBaselineProject(viewer.company.modules, viewer.project)
					? availableColumns.concat([
							{
								name: 'show-scoping-baseline-info',
								checked: showBaselineInfo,
								translationId: 'scoping.show_scoping_baseline_info',
							},
					  ])
					: availableColumns,
			onSelect: selected => theEyeSelector(selected),
			openRight: true,
			ignoreScroll: true,
		};
		leftContent.push(theEye);

		const csv = {
			type: ELEMENT_TYPE.CSV,
			callback: () => (hasFeatureFlag('new_csv_format', viewer.availableFeatureFlags) ? exportCSVNew() : exportCSV()),
			style: BUTTON_STYLE.OUTLINE,
			color: BUTTON_COLOR.LIGHTGREY,
			text: formatMessage({id: 'common.export-csv'}),
			tooltipEnabled: true,
			tooltipProps: {
				autoPlace: true,
				grey: true,
				infoText: formatMessage({id: 'common.export-csv'}),
			},
			disabled: !lazyDataFetched,
		};
		leftContent.push(csv);

		if (projectLocked) {
			const indicator = {
				type: ELEMENT_TYPE.INDICATOR,
				status: viewer.project.status,
			};
			leftContent.push(indicator);
		}

		const search = {
			type: ELEMENT_TYPE.SEARCH_LAZY,
			onChange: searchString => setSearchFilterValue(searchString),
			placeholder: intl.formatMessage({id: 'global_search.placeholder'}),
		};

		rightContent.push(search);

		const taskFilters = [
			FILTER_TYPE.PROJECT_PERSON,
			FILTER_TYPE.ROLE,
			FILTER_TYPE.PROJECT_STATUS_COLUMN,
			FILTER_TYPE.APPROUVED,
			FILTER_TYPE.DEADLINE,
			FILTER_TYPE.DEPENDENCIES,
			FILTER_TYPE.INDICATOR,
			FILTER_TYPE.LABEL,
			FILTER_TYPE.PROJECT_PHASE,
			FILTER_TYPE.RECENT_ACTIVITY,
		];

		if (viewer.company.teams.edges.length > 0) {
			taskFilters.push(FILTER_TYPE.TEAM);
		}

		if (viewer.project && viewer.project.sprintTimeBox) {
			taskFilters.push(FILTER_TYPE.PROJECT_SPRINT);
			taskFilters.push(FILTER_TYPE.SPRINT_CATEGORY);
		}

		if (viewer.project.useTaskOwner) {
			taskFilters.push(FILTER_TYPE.PROJECT_OWNER);
		}

		if (viewer.project.useTaskFollowers) {
			taskFilters.push(FILTER_TYPE.PROJECT_FOLLOWER);
		}

		if (viewer.project && viewer.project.taskLevels === 2) {
			taskFilters.push(FILTER_TYPE.SUB_TASKS);
		}

		if (shouldShowOverrunPrediction) {
			taskFilters.push(FILTER_TYPE.PREDICTED_OVERRUN);
		}

		const projectFilters = [];
		const peopleFilters = [];
		rightContent.push({
			type: ELEMENT_TYPE.FILTER_V4,
			defaultSection: FILTER_SECTIONS.TASKS,
			projectFilters,
			peopleFilters,
			taskFilters: getFiltersAlphabetically(taskFilters, intl.formatMessage),
			viewer: viewer,
			appliedFiltersName: `project-scoping-filters-v4-${viewer.project.id}`,
			filterSection: FILTER_SECTION.SCOPING,
			onFiltersChange: (_, filterFunctions) => setFilterFunctions(filterFunctions),
			projectId: viewer.project.id,
			companyProjectId: viewer.project.companyProjectId,
		});

		if (createMilestoneAccess) {
			const newMilestonButton = {
				type: ELEMENT_TYPE.BUTTON,
				text: formatMessage({id: 'project_scoping.new-scope-group'}),
				callback: () => addPhase(),
				disabled: projectLocked,
				style: BUTTON_STYLE.OUTLINE,
				color: BUTTON_COLOR.PURPLE,
				userpilot: 'new-milestone-button',
				dataCy: 'new-ui-scoping-custom-button-container',
			};
			rightContent.push(newMilestonButton);
		}

		if (autoSchedulingEnabled && !parentGroup && !projectLocked) {
			const autoScheduleButton = {
				key: 'auto-schedule-button',
				type: ELEMENT_TYPE.BUTTON,
				customClassName: 'auto-scheduling-button',
				autoScheduleStyle: true,
				text: intl.formatMessage({id: 'auto_scheduling.auto_schedule'}),
				callback: () => onAutoScheduleButtonClicked(),
				style: BUTTON_STYLE.FILLED,
				color: BUTTON_COLOR.AI_BLUE,
			};
			rightContent.push(autoScheduleButton);
		}

		const shouldCollapse = [...phasesExpansionMap.values()].some(val => val === true);
		const collapse = {
			type: ELEMENT_TYPE.COLLAPSE,
			collapsed: shouldCollapse,
			toggleCollapse: () => toggleCollapseAll(shouldCollapse),
		};

		rightContent.push(collapse);

		const actionsMenuOptions = [];

		actionsMenuOptions.push({
			key: 'GROUP_BY_PERSONS',
			text: formatMessage({id: 'common.group_by_person'}),
			hotkey: 'Shift + G',
			onClick: () => toggleGroupByPerson(),
			slider: true,
			checked: groupSetting === GROUP_TYPE.PERSON,
		});

		actionsMenuOptions.push({
			key: 'GROUP_BY_ROLE',
			text: formatMessage({id: 'common.group_by_role'}),
			onClick: () => toggleGroupByRole(),
			slider: true,
			checked: groupSetting === GROUP_TYPE.ROLE,
		});

		if (isRetainerProject) {
			actionsMenuOptions.push({
				key: 'SHOW_PERIODS',
				text: 'Show Periods',
				onClick: toggleShowPeriods,
				slider: true,
				checked: showPeriods,
			});
			actionsMenuOptions.push({
				key: 'SHOW_EMPTY_PERIODS',
				text: 'Show Empty Periods',
				onClick: toggleShowEmptyPeriods,
				// locked: !showPeriods,
				slider: true,
				checked: showPeriods && showEmptyPeriods,
			});
		}

		if (actionsMenuOptions.length > 0) {
			const actionsMenu = {
				type: ELEMENT_TYPE.ACTIONS_MENU,
				options: actionsMenuOptions,
				whiteInner: true,
				isWhite: true,
				blackIcon: true,
				userpilot: 'actions-menu-top',
			};

			rightContent.push(actionsMenu);
		}

		return buildThirdLevelNavHeader(leftContent, rightContent);
	};

	const selectedTaskCount = selectedTasks.length;

	const getContextMenu = () => {
		return (
			<GenericTaskContextMenu
				bulkEdit={selectedTaskCount > 0 && hasFeatureFlag('bulk_select_context_menu', viewer.availableFeatureFlags)}
				selectedTasks={selectedTasks}
				task={contextMenuTask}
				viewer={viewer}
				contextMenuPosition={contextMenuPosition}
				closeContextMenu={closeContextMenu}
				unselectTasks={() => setSelectedTasks([])}
			/>
		);
	};

	const canDrop = useMemo(() => {
		return getCanDropFunc(groupSetting, showBaselineInfo);
	}, [groupSetting, showBaselineInfo]);

	const hasFinancialAccess =
		Util.hasFinance(viewer.company.modules) &&
		hasSomePermission([PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION, PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION_REVENUE]);
	const showOverrunPrediction = availableColumns.find(col => col.name === 'show-overrun-prediction')?.checked;

	return (
		<div
			className="section-content project-scoping no-margin"
			style={{flexGrow: 1, display: 'flex', flexDirection: 'column'}}
			data-cy={'scoping-page'}
		>
			{selectedTaskCount > 0 && (
				<BulkSelectPopup
					itemCount={selectedTaskCount}
					counterText={intl.formatMessage({id: 'bulk_edit.tasks_selected'})}
					actionOptions={getBulkUpdateOptions(
						selectedTasks,
						setSelectedTasks,
						viewer.project,
						viewer.company,
						viewer.timerTask,
						viewer.timeStartDate,
						viewer.availableFeatureFlags,
						intl
					)}
					onClose={() => setSelectedTasks([])}
					complexOptions={getComplexBulkOptions(
						variant => showAutomateModal(variant),
						getAdditionalBulkUpdateAutomateOptions(
							selectedTasks,
							setSelectedTasks,
							showOverrunPrediction,
							predictionDataMap,
							viewer.project,
							PERMISSION_TYPE.MILESTONE_UPDATE,
							viewer.availableFeatureFlags,
							intl
						),
						intl
					)}
				/>
			)}
			{getHeaderTitle()}
			{constructHeaderBar()}
			<ProjectScopingTotalsProvider projectData={projectTotalData} settings={{currencySymbol, hasFinancialAccess}}>
				<ProjectScopingTotals />
			</ProjectScopingTotalsProvider>
			{showContextMenu ? getContextMenu() : null}
			<div style={{flexGrow: 1}}>
				<TaskTable
					onToggleExpansion={toggleExpansion}
					groupType={groupSetting}
					showBaselineInfo={showBaselineInfo}
					useTaskHierarchy={sharedOptions.useTaskHierarchy}
					tableData={treeData}
					setSelectedTasks={setSelectedTasks}
					selectedTasks={selectedTasks}
					availableColumns={availableColumns}
					onMoveNode={onMoveNode}
					onVisibilityToggle={toggleExpansion}
					canDrag={canDrag}
					canDrop={canDrop}
					refetch={relay.refetch}
				/>
			</div>
		</div>
	);
};

const ProjectScopingPageQuerry = graphql`
	query ProjectScopingPage_Query($projectId: String, $fetchLazyData: Boolean!) {
		viewer {
			actualPersonId
			component(name: "new_scoping")
			project(id: $projectId) {
				id
			}
			...ProjectScopingPage_viewer @arguments(projectId: $projectId, fetchLazyData: $fetchLazyData)
		}
	}
`;

export {ProjectScopingPageQuerry};

export default withSocketHandling(
	withRouter(
		createRefetchContainer(
			ProjectScopingPage,
			{
				viewer: graphql`
					fragment ProjectScopingPage_viewer on Viewer
					@argumentDefinitions(projectId: {type: "String"}, fetchLazyData: {type: "Boolean!"}) {
						id
						language
						email
						firstName
						lastName
						timerStartDate
						actualPersonId
						harvestUser
						unit4User
						excludeFromCompanyLockedPeriod
						submitLockedDateYear
						submitLockedDateMonth
						submitLockedDateDay
						availableFeatureFlags {
							key
						}
						timerTask {
							id
						}
						company {
							modules {
								moduleType
							}
							allPersons(first: 1000000, onlyActive: true) @connection(key: "Company_allPersons", filters: []) {
								edges {
									node {
										id
										firstName
										lastName
									}
								}
							}
							id
							currency
							harvestEnabled
							unit4Enabled
							jiraCloudEnabled
							lockedPeriodYear
							lockedPeriodMonth
							lockedPeriodDay
							labels(first: 1000000) {
								edges {
									node {
										id
										name
										color
									}
								}
							}

							roles(first: 1000000) {
								edges {
									...RoleDropdown_roles
									node {
										id
										name
									}
								}
							}
							teams(first: 1000000) {
								edges {
									node {
										id
										name
										teamPersons(first: 1000) {
											edges {
												node {
													id
													person {
														id
													}
												}
											}
										}
									}
								}
							}
						}
						filters(first: 1000000, projectId: $projectId, filterSection: SCOPING)
							@connection(key: "Viewer_filters", filters: []) {
							edges {
								node {
									id
									name
									section
									value
									updatedAt
								}
							}
						}
						projects(first: 1000000) {
							edges {
								node {
									id
									companyProjectId
									name
									status
									isInProjectGroup
									projectColor
								}
							}
						}
						project(id: $projectId) {
							baselineTargetPrice
							baselineTargetPriceNoExpenses
							baselineTargetMinutes
							id
							name
							budgetWork
							budgetType
							sprintTimeBox
							isInProjectGroup
							useBaseline
							projectGroupId
							status
							companyProjectId
							estimationUnit
							timeRegistrations(first: 10000000, onlyProjectRegistrations: true)
								@connection(key: "Project_timeRegistrations", filters: [])
								@include(if: $fetchLazyData) {
								edges {
									node {
										id
										minutesRegistered
										invoiced
										price
										updatedAt
										year
										month
										day
										retainerConflictHandled
										task {
											id
										}
										person {
											id
										}
									}
								}
							}
							retainerPeriods(first: 100000) {
								edges {
									node {
										id
										name
										startYear
										startMonth
										startDay
										endYear
										endMonth
										endDay
										available
										periodLength
										periodPeriodicity
										periodPriceAmount
										periodHoursAmount
										periodBudgetType
										periodSettingIgnoreForBilling
										periodSettingSubtractValue
										periodSettingRollValue
										periodSettingAddExpenses
										periodDifferencePriceAmount
										periodDifferenceHoursAmount
										sharedPeriodDifferenceHoursAmount
										sharedPeriodDifferencePriceAmount
										ignoredRolloverHours
										ignoredRolloverPrice
										periodLocked
										periodLockedTime
									}
								}
							}
							minutesPerEstimationPoint
							projectColor
							projectStartYear
							projectStartMonth
							projectStartDay
							projectEndYear
							projectEndMonth
							projectEndDay
							useManualAllocations
							remainingAutoCalculated
							isJiraProject
							useTaskOwner
							useTaskFollowers
							taskLevels
							useTaskHierarchy
							useManualAllocations
							vstsProject
							vstsAccount
							vstsTwoWaySync
							jiraSubtaskType
							jiraCloudProject {
								id
							}
							jiraCloudEpics {
								id
							}
							jiraServerProject {
								id
							}
							statusColumnsV2(first: 1000000) {
								edges {
									node {
										id
										name
										order
										jiraStatusId
										category
									}
								}
							}
							rateCard {
								id
								currency
								rates(first: 10000) {
									edges {
										node {
											id
											rate
											defaultRate
											startDate
											role {
												id
											}
										}
									}
								}
							}
							projectPersons(first: 1000000) {
								edges {
									node {
										id
										role {
											id
											name
										}
										person {
											id
											firstName
											lastName
											fullName
											initials
											profilePictureId
											profilePictureDefaultId
											active
											role {
												id
												name
											}
											client {
												id
											}
										}
									}
								}
							}
							phases(first: 1000000) @connection(key: "Project_phases") {
								edges {
									node {
										baselineTargetPrice
										baselineTargetPriceNoExpenses
										baselineTargetMinutes
										inheritDatesFrom
										phaseBaselineRoles {
											edges {
												node {
													role {
														id
													}
													baselineMinutes
													baselinePrice
												}
											}
										}
										id
										name
										startYear
										startMonth
										startDay
										deadlineDay
										deadlineMonth
										deadlineYear
										unit4Id
										jiraId
										phasePersons(first: 10000) {
											edges {
												node {
													id
													availableMinutes
													scheduledMinutes
													person {
														id
													}
												}
											}
										}
									}
								}
							}
							sprints(first: 1000000) {
								edges {
									node {
										id
										name
										endYear
										endMonth
										endDay
										startYear
										startMonth
										startDay
									}
								}
							}
							tasks(first: 100000, simpleResponse: true) @connection(key: "Project_tasks", filters: []) {
								edges {
									node {
										id
										userCanDeleteTask
										userCantDeleteTaskReason
										hasInvoicedTime
										hasLockedTime
										hasTimeRegistrations
										approved
										name
										description
										sortOrder
										done
										billable
										blocked
										progress @include(if: $fetchLazyData)
										bug
										latestUiUpdateAt
										startFrom
										deadlineFrom
										startYear
										startMonth
										startDay
										deadlineDay
										deadlineMonth
										deadlineYear
										estimateLow
										estimateHigh
										estimateForecast
										estimateForecastPrice
										currentPrice @include(if: $fetchLazyData)
										companyTaskId
										timeLeft @include(if: $fetchLazyData)
										deadlineFrom
										canStart
										canBeSetToDone
										approved
										highPriority
										hasDependency
										jiraId
										vstsId
										isUnit4Task
										subTaskCount
										parentTaskId
										hasChildren
										favoured
										owner {
											id
											permissions
										}
										followers {
											id
											permissions
										}
										phase {
											id
											name
											startYear
											startMonth
											startDay
											deadlineDay
											deadlineMonth
											deadlineYear
										}
										role {
											id
											name
										}
										sprint {
											id
											name
											startYear
											startMonth
											startDay
											endYear
											endMonth
											endDay
										}
										statusColumnV2 {
											id
											name
											category
										}
										project {
											id
											billable
											estimationUnit
											minutesPerEstimationPoint
											projectEndYear
											projectEndMonth
											projectEndDay
											budgetType
											name
											status
											isJiraProject
											vstsProject
											vstsAccount
											vstsTwoWaySync
											projectColor
											companyProjectId
											harvestProject {
												id
											}
											unit4Project {
												id
												activitiesEnabled
											}
											timelogProject {
												id
											}
										}
										taskLabels {
											id
											label {
												id
												name
												color
											}
										}
										timeRegistrations(first: 10000)
											@connection(key: "Task_timeRegistrations", filters: [])
											@include(if: $fetchLazyData) {
											edges {
												node {
													id
													minutesRegistered
													price
													invoiced
													year
													month
													day
													retainerConflictHandled
													person {
														id
														role {
															id
														}
													}
												}
											}
										}
										taskDismissedNotifications {
											taskOverrunPredictionDismissed
										}
										assignedPersons {
											id
											firstName
											lastName
											fullName
											active
											email
											profilePictureId
											profilePictureDefaultId
											permissions
										}
										thisTaskDependsOn(first: 1000) {
											edges {
												node {
													id
													type
													thisDependsOnTask {
														id
														name
														deadlineYear
														deadlineMonth
														deadlineDay
														startYear
														startMonth
														startDay
													}
												}
											}
										}
									}
								}
							}
						}
					}
				`,
			},
			graphql`
				query ProjectScopingPageRefetchQuery($projectId: String, $fetchLazyData: Boolean!) {
					viewer {
						component(name: "new_scoping")
						...ProjectScopingPage_viewer @arguments(projectId: $projectId, fetchLazyData: $fetchLazyData)
					}
				}
			`
		)
	)
);
